import { ProductData } from '@/common/types/product.type'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { ControlledByType } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { z } from 'zod'

export const entryProductCurrency = z.object({
  id: z.string().optional().nullable(),
  product: z.custom<ProductData>(),
  total_value: z.string().refine((value) => !!value && unFormatCurrency(value) > 0, { message: 'Campo obrigatório' }),
})

export const entryProductQuantity = z.object({
  id: z.string().optional().nullable(),
  product: z.custom<ProductData>(),
  total_quantity: z
    .string()
    .refine((value) => !!value && unFormatUnknownUnit(value) > 0, { message: 'Campo obrigatório' }),
})

export const entryProductCategoryCurrencySchema = z.object({
  id: z.string().optional().nullable(),
  product_category: z.custom<ProductCategoryData>(),
  total_value: z.string().refine((value) => !!value && unFormatCurrency(value) > 0, { message: 'Campo obrigatório' }),
  entry_products: z.array(entryProductCurrency),
})

export const entryProductCategoryQuantitySchema = z.object({
  id: z.string().optional().nullable(),
  product_category: z.custom<ProductCategoryData>(),
  total_quantity: z
    .string()
    .refine((value) => !!value && unFormatUnknownUnit(value) > 0, { message: 'Campo obrigatório' }),
  entry_products: z.array(entryProductQuantity),
})

export const entryProduct = entryProductCurrency
export const entryProductCategorySchema = entryProductCategoryCurrencySchema

export const updateCurrencySchema = z.object({
  office_number: z.string().optional().nullable(),
  government_allocation: z
    .object({
      start_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
      end_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
    })
    .optional()
    .nullable(),
  total_value: z.string().optional().nullable(),
  entry_product_categories: z.array(entryProductCategoryCurrencySchema),
})

export const updateQuantitySchema = z.object({
  office_number: z.string().optional().nullable(),
  government_allocation: z
    .object({
      start_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
      end_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
    })
    .optional()
    .nullable(),
  total_quantity: z.string().optional().nullable(),
  entry_product_categories: z.array(entryProductCategoryQuantitySchema),
})

export const updateSchema = updateCurrencySchema

export const getUpdateSchema = (controlledBy: ControlledByType) => {
  return controlledBy === 'quantity' ? updateQuantitySchema : updateCurrencySchema
}

export type GovernmentAllocationEntryUpdateValidationSchema = z.infer<typeof updateSchema>
export type GovernmentAllocationEntryUpdateCurrencyValidationSchema = z.infer<typeof updateCurrencySchema>
export type GovernmentAllocationEntryUpdateQuantityValidationSchema = z.infer<typeof updateQuantitySchema>
export type GovernmentAllocationEntryProductValidationSchema = z.infer<typeof entryProduct>
export type GovernmentAllocationEntryProductCategoryValidationSchema = z.infer<typeof entryProductCategorySchema>
export type GovernmentAllocationEntryProductCategoryCurrencyValidationSchema = z.infer<
  typeof entryProductCategoryCurrencySchema
>
export type GovernmentAllocationEntryProductCategoryQuantityValidationSchema = z.infer<
  typeof entryProductCategoryQuantitySchema
>
